import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  IconButton,
  InputBase,
  Button,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import AddIcon from "@material-ui/icons/Add";
import DescriptionIcon from "@material-ui/icons/Description";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";

import LinearWithValueLabel from "../MessageInputCustom/ProgressBarCustom";
import { i18n } from "../../translate/i18n";

/**
 * WhatsApp Web / Telegram-style media preview before sending.
 *
 * Behaviour:
 *   - Opens whenever there is at least one file pending to be sent.
 *   - Shows a real visual preview per file type (image/video/audio/file).
 *   - Lets the user add a caption that gets sent as the message body.
 *   - Lets the user add MORE files via the "+" thumbnail button.
 *   - Lets the user remove individual files from the queue.
 *   - Cancel (X) clears the queue without sending; Send dispatches.
 *
 * The actual upload + Compressor + form-data plumbing lives in the
 * parent's `handleUploadMedia(caption)` — this component is purely UX.
 */

const MAX_BODY_LENGTH = 4096; // WhatsApp caption limit

const useStyles = makeStyles((theme) => ({
  // Soft, semi-transparent backdrop so the conversation stays visible
  // through the overlay (WhatsApp / Telegram-style "live on top of chat"
  // feel rather than the default opaque MUI backdrop).
  backdrop: {
    background: "rgba(15, 23, 42, 0.45)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
  },
  dialogPaper: {
    background:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.96)"
        : "rgba(17, 27, 33, 0.94)", // dark glass — like WhatsApp Web preview
    color: "#f1f5f9",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.06)",
    margin: 24,
    [theme.breakpoints.down("sm")]: {
      margin: 8,
      borderRadius: 12,
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    color: "#f1f5f9",
  },
  closeBtn: {
    color: "#cbd5e1",
  },
  title: {
    fontWeight: 600,
    fontSize: 15,
    color: "#f1f5f9",
  },
  previewArea: {
    background: "transparent", // see paper glass underneath
    minHeight: 280,
    maxHeight: "55vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    overflow: "hidden",
  },
  imgPreview: {
    maxWidth: "100%",
    maxHeight: "55vh",
    objectFit: "contain",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
  },
  videoPreview: {
    maxWidth: "100%",
    maxHeight: "55vh",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
  },
  audioPreview: {
    width: "min(100%, 420px)",
  },
  fileFallback: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    color: "#f1f5f9",
    textAlign: "center",
    padding: 24,
  },
  fileIcon: {
    fontSize: 96,
    opacity: 0.9,
    color: "#e2e8f0",
  },
  fileName: {
    fontWeight: 600,
    fontSize: 16,
    wordBreak: "break-all",
    color: "#f1f5f9",
  },
  fileSize: {
    fontSize: 13,
    color: "#cbd5e1",
  },
  typeBadge: {
    position: "absolute",
    top: 16,
    left: 16,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#ffffff",
  },
  badgeImage: { background: "#16a34a" },
  badgeVideo: { background: "#eab308" },
  badgeAudio: { background: "#0ea5e9" },
  badgeFile: { background: "#9333ea" },
  caption: {
    padding: "12px 14px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  captionInput: {
    width: "100%",
    fontSize: 14,
    padding: "8px 12px",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 10,
    background: "rgba(255,255,255,0.05)",
    color: "#f1f5f9",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      background: "rgba(255,255,255,0.08)",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: "rgba(241, 245, 249, 0.55)",
    },
  },
  thumbStrip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    overflowX: "auto",
    overscrollBehaviorX: "contain",
    WebkitOverflowScrolling: "touch",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  thumb: {
    position: "relative",
    width: 56,
    height: 56,
    flexShrink: 0,
    borderRadius: 8,
    overflow: "hidden",
    cursor: "pointer",
    border: "2px solid transparent",
    background: "rgba(255,255,255,0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  thumbActive: {
    borderColor: theme.palette.primary.main,
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  thumbIcon: {
    color: "#cbd5e1",
  },
  thumbRemove: {
    position: "absolute",
    top: 2,
    right: 2,
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 12,
    lineHeight: 1,
    border: 0,
    "&:hover": { background: "rgba(0,0,0,0.85)" },
  },
  addThumb: {
    width: 56,
    height: 56,
    flexShrink: 0,
    borderRadius: 8,
    border: "2px dashed rgba(255,255,255,0.25)",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#cbd5e1",
    cursor: "pointer",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    padding: "10px 14px",
    borderTop: "1px solid rgba(255,255,255,0.08)",
  },
  footerInfo: {
    flex: 1,
    fontSize: 12,
    color: "#94a3b8",
  },
  cancelBtn: {
    color: "#cbd5e1",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 8,
    fontWeight: 600,
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
    },
  },
  sendBtn: {
    // Always hardcode the teal — never use theme.palette.primary.main
    // because that can be a light color that makes white text invisible.
    background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    color: "#ffffff",
    fontWeight: 700,
    borderRadius: 8,
    boxShadow: "0 4px 14px rgba(18,140,126,0.45)",
    "&:hover": {
      background: "linear-gradient(135deg, #25d366 0%, #0e7a71 100%)",
      boxShadow: "0 6px 18px rgba(18,140,126,0.55)",
    },
    "&:disabled": {
      background: "rgba(255,255,255,0.12)",
      color: "rgba(255,255,255,0.4)",
      boxShadow: "none",
    },
  },
  warning: {
    background: "#fef3c7",
    color: "#92400e",
    fontSize: 12,
    padding: "6px 14px",
    borderTop: "1px solid #fde68a",
  },
}));

const MAX_FILE_SIZE_MB = 16; // WhatsApp Business limit

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i ? 1 : 0)} ${sizes[i]}`;
};

const guessKind = (file) => {
  if (!file?.type) return "file";
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  return "file";
};

/**
 * Builds a stable object-URL per file so the preview <img>/<video>/<audio>
 * doesn't recreate the URL on every render (which would re-fetch the blob,
 * cause flicker, and leak memory).
 *
 * URLs are keyed by File identity (not by the files array reference) so
 * adding/removing one file from the queue only creates/revokes the URL for
 * that file — every other file keeps its existing blob URL and its <img>
 * doesn't re-decode/flash.
 */
const useObjectUrls = (files) => {
  const cacheRef = useRef(new Map()); // File -> objectURL

  const urls = useMemo(() => {
    const cache = cacheRef.current;
    const next = new Map();

    const result = files.map((f) => {
      const kind = guessKind(f);
      if (kind !== "image" && kind !== "video" && kind !== "audio") {
        return null;
      }
      if (cache.has(f)) {
        const existing = cache.get(f);
        next.set(f, existing);
        return existing;
      }
      try {
        const url = URL.createObjectURL(f);
        next.set(f, url);
        return url;
      } catch (_) {
        return null;
      }
    });

    // Revoke URLs for files no longer in the queue.
    cache.forEach((url, file) => {
      if (!next.has(file)) {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {
          /* ignore */
        }
      }
    });

    cacheRef.current = next;
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // Revoke everything on unmount.
  useEffect(() => {
    return () => {
      cacheRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {
          /* ignore */
        }
      });
    };
  }, []);

  return urls;
};

const TypeBadge = ({ kind, classes }) => {
  const map = {
    image: { label: "Image", cls: classes.badgeImage },
    video: { label: "Video", cls: classes.badgeVideo },
    audio: { label: "Audio", cls: classes.badgeAudio },
    file: { label: "Document", cls: classes.badgeFile },
  };
  const { label, cls } = map[kind] || map.file;
  return <span className={`${classes.typeBadge} ${cls}`}>{label}</span>;
};

const FilePreview = ({ file, url, classes }) => {
  const kind = guessKind(file);

  if (kind === "image" && url) {
    return <img src={url} alt={file.name} className={classes.imgPreview} />;
  }
  if (kind === "video" && url) {
    return (
      <video controls src={url} className={classes.videoPreview}>
        <track kind="captions" />
      </video>
    );
  }
  if (kind === "audio" && url) {
    return (
      <div className={classes.fileFallback}>
        <AudiotrackIcon className={classes.fileIcon} />
        <div className={classes.fileName}>{file.name}</div>
        <audio controls src={url} className={classes.audioPreview} />
      </div>
    );
  }

  // Document / unknown — show big icon + name + size so it's obvious WHAT
  // is about to be sent (prevents the "wrong PDF" mistake).
  const Icon =
    file?.type === "application/pdf" ? PictureAsPdfIcon : DescriptionIcon;
  return (
    <div className={classes.fileFallback}>
      <Icon className={classes.fileIcon} />
      <div className={classes.fileName}>{file.name}</div>
      <div className={classes.fileSize}>
        {formatBytes(file.size)} · {file.type || "file"}
      </div>
    </div>
  );
};

const MediaPreviewModal = ({
  files = [],
  onCancel,
  onSend,
  onAddMore,
  onRemoveAt,
  loading = false,
  progress = 0,
}) => {
  const classes = useStyles();
  const [activeIdx, setActiveIdx] = useState(0);
  const [caption, setCaption] = useState("");

  // When the file list shrinks, snap activeIdx back into range.
  useEffect(() => {
    if (activeIdx >= files.length) {
      setActiveIdx(Math.max(0, files.length - 1));
    }
  }, [files.length, activeIdx]);

  const urls = useObjectUrls(files);
  const open = files.length > 0;
  const active = files[activeIdx];
  const activeUrl = urls[activeIdx];
  const activeKind = active ? guessKind(active) : "file";

  const oversizedCount = files.filter(
    (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024
  ).length;

  const handleSend = (e) => {
    e?.preventDefault?.();
    if (loading) return;
    onSend(caption.trim());
  };

  const handleAddMore = (e) => {
    if (!onAddMore) return;
    const file = e?.target?.files;
    if (file && file.length) {
      onAddMore(Array.from(file));
      e.target.value = ""; // allow re-selecting the same file later
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      classes={{ paper: classes.dialogPaper }}
      BackdropProps={{ classes: { root: classes.backdrop } }}
    >
      <div className={classes.header}>
        <div className={classes.title}>
          {files.length > 1
            ? `${i18n.t("messagesInput.preview.titleMulti", "Send media")} (${
                files.length
              })`
            : i18n.t("messagesInput.preview.titleOne", "Send media")}
        </div>
        <IconButton
          size="small"
          onClick={onCancel}
          disabled={loading}
          aria-label="close"
          className={classes.closeBtn}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.previewArea} style={{ position: "relative" }}>
        {active && <TypeBadge kind={activeKind} classes={classes} />}
        {active ? (
          <FilePreview file={active} url={activeUrl} classes={classes} />
        ) : (
          <CircularProgress />
        )}
      </div>

      {oversizedCount > 0 && (
        <div className={classes.warning}>
          {oversizedCount === 1
            ? i18n.t(
                "messagesInput.preview.oversizedOne",
                `One file is larger than ${MAX_FILE_SIZE_MB} MB and may be rejected by WhatsApp.`
              )
            : i18n.t(
                "messagesInput.preview.oversizedMany",
                `${oversizedCount} files are larger than ${MAX_FILE_SIZE_MB} MB and may be rejected by WhatsApp.`
              )}
        </div>
      )}

      {loading && (
        <div style={{ padding: "10px 14px" }}>
          <LinearWithValueLabel progress={progress} />
        </div>
      )}

      <div className={classes.caption}>
        <InputBase
          fullWidth
          multiline
          maxRows={4}
          placeholder={i18n.t(
            "messagesInput.preview.captionPlaceholder",
            "Add a caption (optional)…"
          )}
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, MAX_BODY_LENGTH))}
          className={classes.captionInput}
          disabled={loading}
          inputProps={{ maxLength: MAX_BODY_LENGTH }}
        />
      </div>

      {(files.length > 1 || onAddMore) && (
        <div className={classes.thumbStrip}>
          {files.map((f, idx) => {
            const k = guessKind(f);
            return (
              <div
                key={`${f.name}-${idx}`}
                className={`${classes.thumb} ${
                  idx === activeIdx ? classes.thumbActive : ""
                }`}
                onClick={() => setActiveIdx(idx)}
              >
                {k === "image" && urls[idx] ? (
                  <img src={urls[idx]} alt="" className={classes.thumbImg} />
                ) : k === "video" ? (
                  <DescriptionIcon className={classes.thumbIcon} />
                ) : k === "audio" ? (
                  <AudiotrackIcon className={classes.thumbIcon} />
                ) : (
                  <DescriptionIcon className={classes.thumbIcon} />
                )}
                {!loading && files.length > 1 && (
                  <button
                    type="button"
                    className={classes.thumbRemove}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveAt && onRemoveAt(idx);
                    }}
                    aria-label="remove"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
          {onAddMore && !loading && (
            <label className={classes.addThumb}>
              <AddIcon />
              <input
                type="file"
                multiple
                hidden
                onChange={handleAddMore}
              />
            </label>
          )}
        </div>
      )}

      <div className={classes.footer}>
        <div className={classes.footerInfo}>
          {active &&
            `${active.name} · ${formatBytes(active.size)}${
              files.length > 1 ? ` · ${files.length} files` : ""
            }`}
        </div>
        <Button
          onClick={onCancel}
          disabled={loading}
          className={classes.cancelBtn}
        >
          {i18n.t("messagesInput.preview.cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSend}
          disabled={loading || files.length === 0}
          className={classes.sendBtn}
        >
          {loading
            ? i18n.t("messagesInput.preview.sending", "Sending…")
            : i18n.t("messagesInput.preview.send", "Send")}
        </Button>
      </div>
    </Dialog>
  );
};

export default MediaPreviewModal;
